import type { StorefrontItem } from '../api/types.ts';
import { createStore } from '../core/store.ts';

/**
 * Запрос адреса на первом «в корзину».
 *
 * Витрина открывается без адреса, от склада по умолчанию. Первый товар поднимает лист с картой
 * над тем экраном, где нажали «плюс»: склад адреса решает, что стоит на полке и доставляем ли
 * вообще. Товар, ради которого спросили, ложится в корзину после выбора адреса.
 */
export type AddressGateRequest = { id: number; item: StorefrontItem };

export const addressGateStore = createStore<AddressGateRequest | null>(null);

let nextId = 1;

export function askAddress(item: StorefrontItem): void {
  addressGateStore.set({ id: nextId++, item });
}

/**
 * Подтверждение возраста для товаров 18+.
 *
 * Дата рождения лежит в профиле на сервере, здесь её зеркало: корзина решает по ней, класть
 * ли товар, а лист подтверждения поднимается над тем экраном, где нажали «плюс». Товар, ради
 * которого спросили, ложится в корзину после ответа.
 */
export const ADULT_AGE = 18;

export type AgeVerdict = 'unknown' | 'underage' | 'adult';

/** Товар, который ждёт подтверждения; null — проверка перед оформлением. */
export type AgeGateRequest = { id: number; item: StorefrontItem | null };

export type AgeGateState = {
  /** Дата рождения из профиля, `YYYY-MM-DD`; у гостя — названная на устройстве. */
  birthDate: string | null;
  pending: AgeGateRequest | null;
};

const GUEST_BIRTH_KEY = 'trotty10.client.guest-birth-date';

export const ageGateStore = createStore<AgeGateState>({ birthDate: localStorage.getItem(GUEST_BIRTH_KEY), pending: null });

/** Дата, названная гостем до входа: при первом входе уходит в профиль. */
export function guestBirthDate(): string | null {
  return localStorage.getItem(GUEST_BIRTH_KEY);
}

export function setGuestBirthDate(value: string | null): void {
  if (value) localStorage.setItem(GUEST_BIRTH_KEY, value);
  else localStorage.removeItem(GUEST_BIRTH_KEY);
}

export function setMirroredBirthDate(birthDate: string | null): void {
  ageGateStore.set((state) => ({ ...state, birthDate }));
}

/** Полных лет на сегодня по дате `YYYY-MM-DD`. */
export function fullYears(birthDate: string, now: Date = new Date()): number | null {
  const [year, month, day] = birthDate.split('-').map(Number);
  if (!year || !month || !day) return null;
  let years = now.getFullYear() - year;
  if (now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day)) years -= 1;
  return years;
}

export function ageVerdict(state: AgeGateState = ageGateStore.get()): AgeVerdict {
  if (!state.birthDate) return 'unknown';
  const years = fullYears(state.birthDate);
  if (years === null) return 'unknown';
  return years >= ADULT_AGE ? 'adult' : 'underage';
}

export function askAge(item: StorefrontItem | null): void {
  ageGateStore.set((state) => ({ ...state, pending: { id: nextId++, item } }));
}

export function dismissAgeGate(): void {
  ageGateStore.set((state) => ({ ...state, pending: null }));
}
